import { FC, useMemo } from 'react';
import styled from 'styled-components/native';

import Button, { ButtonType } from '../../../elements/CommonButton';
import { globalNormalDialogProxy } from '../../../elements/Dialog';
import NoScaleText from '../../../elements/NoScaleText';
import { useTranslation } from '../../../i18n';
import { CheckOutPages } from '../../../types/Common';
import { showNumberWithComma } from '../../../utils/stringHelper';
import { useData } from './hooks';

interface Props {
  disableBtn?: boolean;
  isLoading: boolean;
  onPress: () => void;
  page?: CheckOutPages;
}

const CheckoutSection: FC<Props> = ({
  disableBtn,
  isLoading,
  onPress,
  page,
}) => {
  const { t } = useTranslation();
  const { totalQuantity, totalPrice, symbol, hasDiscount, selectedMethod } =
    useData();

  const isDisabled = totalQuantity < 1 || disableBtn;

  const handleOnPress = () => {
    if (page === 'cart' && !selectedMethod) {
      globalNormalDialogProxy.open({
        titleText: t('alert'),
        contentText: t('select-shipping-method-alert'),
        positiveText: t('alert-default-positive-text'),
      });

      return;
    }

    onPress();
  };

  const buttonText = useMemo(() => {
    switch (page) {
      case 'cart':
        return totalQuantity > 0
          ? t('go-checkout') + `(${totalQuantity})`
          : t('go-checkout');
      case 'checkout':
        return t('next-step');
      case 'payment':
        return t('confirm-submit-order');
      case 'combo':
        return totalQuantity > 0 ? t('cart') + `(${totalQuantity})` : t('cart');
      default:
        return;
    }
  }, [t, totalQuantity, page]);

  const shippingHint = useMemo(() => {
    switch (page) {
      case 'cart':
        return t('cart-shipping-hint');
      case 'combo':
        return t('total-combo-items', { totalQuantity });
      default:
        return hasDiscount
          ? t('freight-discount-included')
          : t('freight-included');
    }
  }, [totalQuantity, hasDiscount, page, t]);

  const testID = useMemo(() => {
    switch (page) {
      case 'cart':
        return 'checkoutBtn';
      case 'payment':
        return 'submitBtn';
      case 'checkout':
        return 'goPayingBtn';
      default:
        return;
    }
  }, [page]);

  return (
    <S.Container>
      <S.Left>
        <S.PriceSection>
          <S.PriceTitle>{t('products-total')}</S.PriceTitle>
          <S.Symbol>{symbol} </S.Symbol>
          <S.Price>{totalPrice && showNumberWithComma(totalPrice)}</S.Price>
        </S.PriceSection>
        <S.Description>{shippingHint}</S.Description>
      </S.Left>
      <S.Right>
        <Button
          text={buttonText}
          buttonType={ButtonType.PRIMARY}
          style={{ borderRadius: 8 }}
          disabled={isDisabled || isLoading}
          onPress={handleOnPress}
          isLoading={isLoading}
          testID={testID}
        />
      </S.Right>
    </S.Container>
  );
};

const S = {
  Container: styled.View`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
  `,
  Left: styled.View`
    align-items: flex-start;
    justify-content: center;
  `,
  Right: styled.View`
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  `,
  PriceSection: styled.View`
    flex-direction: row;
    align-items: center;
    margin-bottom: 2px;
  `,
  PriceTitle: styled(NoScaleText)`
    font-size: 13px;
    margin-right: 8px;
    color: ${({ theme }) => theme.palette.warrior};
  `,
  Symbol: styled(NoScaleText)`
    font-size: 16px;
    color: ${({ theme }) => theme.palette.coral};
  `,
  Price: styled(NoScaleText)`
    font-size: 20px;
    border: 2px solid transparent;
    color: ${({ theme }) => theme.palette.coral};
  `,
  Description: styled(NoScaleText)`
    font-size: 13px;
    color: ${({ theme }) => theme.palette.neutralDark};
  `,
};

export default CheckoutSection;
